/**
 * Event Operations tab (SPEC step 6 + 7) — the mobile operator surface, on the event page as a tab.
 * The one place staff run the event: see everyone + their guests, their live check-in + REWARD STATE
 * (rep-facing vocabulary, step 7), fix a reward to the current-correct tier, forward-to-a-friend, and
 * register a walk-in. Gated (the tab only mounts for operators; every RPC is authorized server-side).
 *
 * CONCURRENCY: a live event has attendees self-checking-in (which fires auto-escalation) while staff
 * watch. The roster AUTO-REFRESHES on an interval + on window focus + after any operator action, so the
 * list tracks live arrivals. Every write is safe regardless of how stale the on-screen snapshot is:
 * fix_event_attendee_reward re-derives the tier server-side under a contact lock, and the assignment
 * core takes a voucher FOR UPDATE — so a stale roster can never drive a wrong or racing write.
 */
import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getEventOperationsRoster,
  fixEventAttendeeReward,
  ratchetOrStartAttendeeReward,
  creditReferralReward,
  resendAttendeeQr,
  showAttendeeQr,
  createEventReferralLink,
  downloadEventCheckinRosterCsv,
  type OperationsRosterRow,
} from "../../../services/eventOperations.service";
import {
  boothScanCheckin,
  type BoothScanResult,
} from "../../../services/eventFieldCheckin.service";
import { extractScanCode } from "../../../services/selfCheckin.service";
import { supabase } from "../../../services/supabase";
import { rewardStatus } from "../../../utils/eventRewardStatus";
import QrScanner from "../../checkin/QrScanner";

interface Props {
  eventId: string;
  onWalkIn: () => void;
  /**
   * Whether scan-admit + walk-in are allowed right now (event is scheduled/in_progress). Audit F2:
   * these consume an admission + fire reward processing, so they're closed outside live status. The
   * server (booth-scan-checkin / event-walkin-register) enforces this too; this is only the UI gate.
   */
  canCheckIn?: boolean;
  /**
   * Event end instant (ISO). When the operator window has closed (now > end_time + 60min), operator
   * controls — scan, walk-in, add value, credit, resend — are locked to match the server (the reward
   * RPCs + booth/walk-in edge fns refuse past this point). The roster + export stay available.
   */
  endTime?: string | null;
}

/** Operator grace: controls stay live until 60min past the event's end (mirrors the server policy). */
const OPERATOR_GRACE_MS = 60 * 60 * 1000;

const REFRESH_MS = 20000;

/** Rep-facing scan outcome (online-only scan-admit for THIS event). */
function scanOutcome(
  ok: boolean,
  r: BoothScanResult,
): { text: string; cls: string } {
  const red = "bg-red-50 text-red-700";
  const gray = "bg-gray-100 text-gray-700";
  if (!ok || r.error) {
    return { text: r.error ?? r.message ?? "Scan failed", cls: red };
  }
  if (r.found === false) {
    return { text: "Code not recognized for this event", cls: gray };
  }
  if (r.admitted && r.already_checked_in) {
    return {
      text: `${r.holder ?? "Attendee"} — already checked in`,
      cls: "bg-blue-100 text-blue-800",
    };
  }
  if (r.admitted) {
    return {
      text: `✓ Checked in${r.holder ? ` — ${r.holder}` : ""}`,
      cls: "bg-green-100 text-green-800",
    };
  }
  return { text: r.message ?? "Done", cls: gray };
}

const ScanPanel: React.FC<{ eventId: string; onAdmitted: () => void }> = ({
  eventId,
  onAdmitted,
}) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{ text: string; cls: string } | null>(
    null,
  );

  async function submit(raw: string) {
    setBusy(true);
    setBanner(null);
    try {
      // Audit F3: extraction is INSIDE the try — extractScanCode() decodes a scanned URL and can throw
      // on a malformed %-sequence (decodeURIComponent). A thrown decode must surface as a red banner,
      // not an unhandled rejection that leaves the panel stuck.
      const c = extractScanCode(raw).trim();
      if (!c) {
        return;
      }
      const { ok, result } = await boothScanCheckin(c, eventId);
      setBanner(scanOutcome(ok, result));
      if (ok && result.admitted) {
        setCode("");
        onAdmitted();
      }
    } catch (e) {
      setBanner({
        text: String((e as { message?: string })?.message ?? "Scan failed"),
        cls: "bg-red-50 text-red-700",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="mb-1 text-xs font-medium text-amber-700">
        Online only — needs a connection (no offline queue here).
      </p>
      <div className="flex gap-2">
        <input
          className="w-full rounded-md border border-gray-300 px-2 py-2 text-base"
          placeholder="Enter or paste ticket code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              void submit(code);
            }
          }}
        />
        <button
          type="button"
          onClick={() => void submit(code)}
          disabled={busy || !code.trim()}
          className="shrink-0 rounded-md bg-gray-800 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {busy ? "…" : "Check in"}
        </button>
      </div>
      <div className="mt-2">
        <QrScanner
          elementId="ops-qr-reader"
          buttonLabel="Scan QR code"
          onDecode={(text) => void submit(text)}
          onError={(m) => setBanner({ text: m, cls: "bg-red-50 text-red-700" })}
        />
      </div>
      {banner && (
        <p
          className={`mt-2 rounded-md px-2 py-1 text-sm font-medium ${banner.cls}`}
        >
          {banner.text}
        </p>
      )}
    </div>
  );
};

const EventOperationsTab: React.FC<Props> = ({
  eventId,
  onWalkIn,
  canCheckIn = true,
  endTime = null,
}) => {
  // Operator window closed once now > end_time + 60min. Recomputed each render; the roster refetches
  // on an interval so a live event that ends while the tab is open locks on the next tick.
  const opLocked =
    !!endTime && Date.now() > new Date(endTime).getTime() + OPERATOR_GRACE_MS;
  const { data, isLoading, error, dataUpdatedAt, refetch, isFetching } =
    useQuery({
      queryKey: ["event-operations-roster", eventId],
      queryFn: () => getEventOperationsRoster(eventId),
      // Once the operator window has closed (opLocked: now > end_time + 60min) the roster is final —
      // no check-ins/walk-ins/ratchets can change it — so stop the 20s poll + focus refetch. This is
      // pure waste otherwise (one network round-trip every 20s per open passed-event tab).
      refetchInterval: opLocked ? false : REFRESH_MS,
      refetchOnWindowFocus: !opLocked,
    });

  // Item 4: show the event's soft max capacity so the operator can see it against the live count.
  // Also carries the title, used only to name the exported CSV file.
  const { data: eventMeta } = useQuery({
    queryKey: ["event-capacity", eventId],
    queryFn: async () => {
      const { data } = await supabase
        .from("events")
        .select("max_attendees, capacity, title")
        .eq("id", eventId)
        .maybeSingle();
      return {
        capacity: (data?.max_attendees ?? data?.capacity ?? null) as
          | number
          | null,
        title: (data?.title ?? null) as string | null,
      };
    },
  });
  const maxCapacity = eventMeta?.capacity ?? null;

  // Insurance-state event (venue state on the insurance list, migration 392 rule)? Then operators can't add
  // voucher value — "+ $20" and "Credit inviter" are hidden, and the server refuses them too (migration 398).
  // Hidden while the answer loads; on a lookup error the buttons show and the server still refuses.
  const { data: isInsuranceState, isLoading: insuranceLoading } = useQuery({
    queryKey: ["event-insurance-state", eventId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("event_is_insurance_state", {
        p_event_id: eventId,
      });
      if (error) {
        throw error;
      }
      return data === true;
    },
  });
  const noAddedValue = insuranceLoading || isInsuranceState === true;

  // Ticket-only event? award_kind='none' means the event issues an admission ticket with NO
  // configured reward (migration 2026-08-11-007). Such events still attach a $0 pending
  // placeholder voucher at registration, which the roster would otherwise show as "Reward
  // pending" — misleading, since no reward is coming automatically. We read it once (the
  // provisioning row is SELECT-able by any authenticated operator, RLS eib_provisioning_select)
  // and relabel those rows honestly. Defaults false (configured-reward behaviour) while loading
  // or when there is no provisioning row.
  const { data: isTicketOnly = false } = useQuery({
    queryKey: ["event-ticketing-award-kind", eventId],
    queryFn: async () => {
      const { data } = await supabase
        .from("event_ticketing_provisioning")
        .select("award_kind")
        .eq("event_id", eventId)
        .maybeSingle();
      return (data?.award_kind ?? null) === "none";
    },
  });

  const [filter, setFilter] = useState("");
  const [showScan, setShowScan] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      await downloadEventCheckinRosterCsv(eventId, eventMeta?.title ?? null);
    } catch (e) {
      const msg = String((e as { message?: string })?.message ?? "");
      setExportError(
        msg.includes("not authorized")
          ? "You don't have permission to export this event."
          : msg.includes("synchronous limit")
            ? "This roster is too large to export here."
            : "Export failed. Please try again.",
      );
    } finally {
      setExporting(false);
    }
  }
  // Audit F2: if the event leaves live status while the tab is open, force the scan panel shut so a
  // now-closed event can't be scanned into (the server also refuses, but don't leave the door ajar).
  React.useEffect(() => {
    if (!canCheckIn) {
      setShowScan(false);
    }
  }, [canCheckIn]);
  const rows = useMemo(() => {
    const list = (data ?? []) as OperationsRosterRow[];
    const q = filter.trim().toLowerCase();
    if (!q) {
      return list;
    }
    return list.filter((a) =>
      `${a.first_name ?? ""} ${a.last_name ?? ""} ${a.email ?? ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [data, filter]);

  const referralsAvailable = (data ?? [])[0]?.referrals_available ?? false;
  const checkedIn = (data ?? []).filter((r) => !!r.checked_in_at).length;
  const updatedSecondsAgo = dataUpdatedAt
    ? Math.max(0, Math.round((Date.now() - dataUpdatedAt) / 1000))
    : null;

  if (error) {
    const msg = String((error as { message?: string })?.message ?? "");
    return (
      <div className="py-6 text-sm text-red-700">
        {msg.includes("not authorized")
          ? "You don't have permission to operate this event."
          : "Could not load the operations roster. Please try again."}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {(data ?? []).length} on roster · {checkedIn} checked in
            {typeof maxCapacity === "number" && (
              <span className="font-normal text-gray-500">
                {" "}
                · {maxCapacity} capacity (soft)
              </span>
            )}
          </p>
          {opLocked ? (
            // Passed event: the roster is final and auto-refresh is off, so there's no live "updated
            // Ns ago" timer to show and nothing to poll for. Static label instead of a ticking control.
            <span className="text-xs text-gray-400">Roster final</span>
          ) : (
            <button
              type="button"
              onClick={() => void refetch()}
              className="text-xs text-blue-600"
            >
              {isFetching
                ? "Refreshing…"
                : updatedSecondsAgo === null
                  ? "Refresh"
                  : `Updated ${updatedSecondsAgo}s ago · Refresh`}
            </button>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={exporting || checkedIn === 0}
            title={
              checkedIn === 0
                ? "No checked-in attendees to export yet"
                : "Download checked-in attendees (with member details) as CSV"
            }
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-40"
          >
            {exporting ? "Exporting…" : "Export"}
          </button>
          <button
            type="button"
            onClick={() => setShowScan((v) => !v)}
            disabled={!canCheckIn || opLocked}
            title={
              opLocked
                ? "This event has ended — check-in is locked"
                : canCheckIn
                  ? undefined
                  : "Check-in opens when the event is live"
            }
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-40"
          >
            {showScan ? "Close scan" : "Scan"}
          </button>
          <button
            type="button"
            onClick={onWalkIn}
            disabled={!canCheckIn || opLocked}
            title={
              opLocked
                ? "This event has ended — walk-in is locked"
                : canCheckIn
                  ? undefined
                  : "Check-in opens when the event is live"
            }
            className="rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            + Walk-in
          </button>
        </div>
      </div>

      {exportError && (
        <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {exportError}
        </p>
      )}

      {opLocked ? (
        <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          This event has ended. Operator controls — scan, walk-in, add value,
          credit, and resend — are locked. The roster below stays viewable and
          exportable.
        </p>
      ) : (
        !canCheckIn && (
          <p className="mb-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
            Scan &amp; walk-in check-in open when this event is scheduled or in
            progress.
          </p>
        )
      )}

      {isInsuranceState === true && !opLocked && (
        <p className="mb-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
          This event is in an insurance state, so voucher value can&rsquo;t be
          added to tickets here (no &ldquo;+ $20&rdquo; or inviter credit).
          Tickets, Show QR and Send QR work as usual.
        </p>
      )}

      {showScan && canCheckIn && !opLocked && (
        <ScanPanel eventId={eventId} onAdmitted={() => void refetch()} />
      )}

      <input
        className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-base"
        placeholder="Search name or email"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      {isLoading ? (
        <p className="py-4 text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-sm text-gray-500">
          {filter ? "No matching attendees." : "No attendees yet."}
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => (
            <AttendeeCard
              key={a.attendee_id}
              row={a}
              eventId={eventId}
              referralsAvailable={referralsAvailable}
              locked={opLocked}
              isTicketOnly={isTicketOnly}
              noAddedValue={noAddedValue}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** Format a date-only ISO (YYYY-MM-DD) as a readable date WITHOUT a timezone shift.
 *  member_start_date is date-only (no clock time), so there is nothing to localize to
 *  the event timezone — building the Date from the Y/M/D parts avoids the classic
 *  "parse as UTC midnight → render a day early in a western tz" bug. */
function formatMemberStartDate(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) {
    return null;
  }
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return dt.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const AttendeeCard: React.FC<{
  row: OperationsRosterRow;
  eventId: string;
  referralsAvailable: boolean;
  /** Event ended (operator window closed) — value/credit/resend actions are locked; the card stays readable. */
  locked: boolean;
  /** Event is ticket-only (award_kind='none') — a plain $0 pending placeholder is relabeled
   *  "Ticket issued — no reward assigned" instead of the misleading "Reward pending". */
  isTicketOnly: boolean;
  /** Insurance-state event (or still loading that answer): hide "+ $20" and "Credit inviter" (mig 398). */
  noAddedValue: boolean;
}> = ({
  row,
  eventId,
  referralsAvailable,
  locked,
  isTicketOnly,
  noAddedValue,
}) => {
  const queryClient = useQueryClient();
  const name =
    `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() ||
    String(row.email ?? "(no name)");
  const isGuest = String(row.attendee_type ?? "") === "guest";
  const checkedIn = !!row.checked_in_at;
  // Authoritative membership = active subscription (server sets is_member_reward_eligible
  // from contacts.has_active_subscription). is_new_member flags a registrant who CONVERTED at
  // this event — their membership started on/after the event's local start date — so an MSR can
  // spot the prospect conversion and reward it via Fix reward / ratchet. 2026-07-31.
  const isMember = row.is_member_reward_eligible;
  const convertedHere = row.is_new_member;
  const memberSince = formatMemberStartDate(row.member_start_date);
  const status = rewardStatus(row, isTicketOnly);

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // On-screen QR (operator shows it so the attendee can photograph it).
  const [qr, setQr] = useState<{
    url: string | null;
    code: string | null;
  } | null>(null);
  // Whether the operator's +$20 shows — read DIRECTLY from the server-authoritative roster flag
  // (post-audit round-2 LOW). `can_start_or_ratchet` already encodes every precondition (checked-in +
  // linked contact + ≤1 live ticket + either an existing overridable monetary reward under the $40 cap
  // OR a from-scratch bootstrap with no voucher yet), computed against the real voucher_id — so React no
  // longer reconstructs "no voucher yet" from a nullable reward_state.
  const canStartOrRatchet = row.can_start_or_ratchet;
  // QR controls (Send = email, Show = on-screen). Both need a checked-in attendee with a contact;
  // Send additionally needs an email address to send to.
  const canShowQr = !!row.checked_in_at && !!row.contact_id;
  const canResendQr = canShowQr && !!row.email;

  async function fixReward() {
    if (!row.needs_reward_fix) {
      return;
    }
    if (
      !window.confirm(
        `Re-issue the correct award for ${name}? This sets their event reward to the tier they currently qualify for.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await fixEventAttendeeReward({
        eventId,
        attendeeId: row.attendee_id,
        commandId: crypto.randomUUID(),
      });
      setMsg(`Reward set: ${res.assigned_offer_name ?? "correct award"}`);
      await queryClient.invalidateQueries({
        queryKey: ["event-operations-roster", eventId],
      });
    } catch (e) {
      setMsg(
        `Couldn't set reward: ${String((e as { message?: string })?.message ?? "error")}`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function addTwenty() {
    if (
      !window.confirm(
        `Add $20 to ${name}'s reward? ${row.ratchets_remaining} of 2 remaining.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      // Bootstraps a token + $0 base voucher for a voucher-less registrant, then the untouched ratchet
      // applies +$20 as this operator.
      const res = await ratchetOrStartAttendeeReward({
        eventId,
        attendeeId: row.attendee_id,
        commandId: crypto.randomUUID(),
      });
      setMsg(
        `Reward now $${res.reward_value} — ${res.ratchets_remaining} ratchet${res.ratchets_remaining === 1 ? "" : "s"} left.`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["event-operations-roster", eventId],
      });
    } catch (e) {
      setMsg(
        `Couldn't add value: ${String((e as { message?: string })?.message ?? "error")}`,
      );
    } finally {
      setBusy(false);
    }
  }

  // Companion seam 4: credit the inviting member for bringing this companion, who bought a membership.
  // Uses the SEPARATE referral budget (credit_referral action → credit_event_referral_reward): +$20 to the
  // INVITER's own reward under the distinct 'referral_credit' source, capped at $40/member/event and NOT
  // sharing the member's personal +$20 ratchet budget. The server re-validates the companion↔inviter link,
  // the event-scoped purchase, active-member inviter, and enforces per-companion idempotency (so a repeat
  // click is a no-op, not a second +$20) — the button also flips to "Credited" once row.referral_credited.
  async function creditInviter() {
    if (!row.inviter_attendee_id) {
      return;
    }
    if (
      !window.confirm(
        `Credit ${row.invited_by_name} with +$20 for bringing ${name}, who bought a membership at this event? This adds $20 to ${row.invited_by_name}'s referral reward (separate from their own reward, capped at $40).`,
      )
    ) {
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await creditReferralReward({
        eventId,
        inviterAttendeeId: row.inviter_attendee_id,
        companionAttendeeId: row.attendee_id,
        commandId: crypto.randomUUID(),
      });
      setMsg(
        res.idempotent_replay
          ? `${row.invited_by_name} was already credited for ${name}.`
          : `Credited ${row.invited_by_name}: referral reward now $${res.reward_value} — ${res.referral_remaining} referral credit${res.referral_remaining === 1 ? "" : "s"} left.`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["event-operations-roster", eventId],
      });
    } catch (e) {
      setMsg(
        `Couldn't credit ${row.invited_by_name}: ${String((e as { message?: string })?.message ?? "error")}`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function resendQr() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await resendAttendeeQr({
        eventId,
        attendeeId: row.attendee_id,
      });
      setMsg(
        res.emailed
          ? "QR ticket emailed to the attendee."
          : `QR not sent${res.reason ? ` (${res.reason})` : ""}.`,
      );
    } catch (e) {
      setMsg(
        `Couldn't send QR: ${String((e as { message?: string })?.message ?? "error")}`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function showQr() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await showAttendeeQr({
        eventId,
        attendeeId: row.attendee_id,
      });
      if (res.qrUrl) {
        setQr({ url: res.qrUrl, code: res.ticketCode ?? null });
      } else {
        setMsg(
          `Couldn't bring up the QR${res.reason ? ` (${res.reason})` : ""}.`,
        );
      }
    } catch (e) {
      setMsg(
        `Couldn't bring up the QR: ${String((e as { message?: string })?.message ?? "error")}`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function getReferral() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await createEventReferralLink(eventId, row.attendee_id);
      if (res.referral_path) {
        setLink(`${window.location.origin}${res.referral_path}`);
      }
    } catch (e) {
      setMsg(
        `Couldn't get referral link: ${String((e as { message?: string })?.message ?? "error")}`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!link) {
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // input stays selectable as a fallback
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-gray-900">{name}</p>
          {row.invited_by_name && (
            <p className="text-xs font-medium text-indigo-600">
              brought by {row.invited_by_name}
            </p>
          )}
          {/* Membership role. Rendered only when the server actually knows it: a null means the
              person isn't tied to a Zuora account, and showing nothing is the honest answer —
              printing "Primary" over a walk-in guest would be a confident wrong one. */}
          {row.is_primary_member !== null && (
            <span
              className={
                row.is_primary_member
                  ? "mt-0.5 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-inset ring-slate-300"
                  : "mt-0.5 inline-block rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-inset ring-violet-200"
              }
              title={
                row.is_primary_member
                  ? "Primary on their U.S. LawShield membership."
                  : "Covered as a secondary on someone else's membership — not the account holder."
              }
            >
              {row.is_primary_member ? "Primary member" : "Secondary member"}
            </span>
          )}
          {row.email ? (
            <p className="truncate text-xs text-gray-400">{row.email}</p>
          ) : null}
          {row.subscribed_products && row.subscribed_products.length > 0 && (
            <div
              className="mt-0.5 flex flex-wrap gap-1"
              title={`Subscribed: ${row.subscribed_products.join(", ")}`}
            >
              {row.subscribed_products.map((p, i) => (
                <span
                  key={i}
                  className="inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
                >
                  {p}
                </span>
              ))}
            </div>
          )}
          {convertedHere && (
            <p className="truncate text-xs font-medium text-emerald-700">
              Converted at this event
              {memberSince ? ` · joined ${memberSince}` : ""}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {checkedIn ? (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              Checked in
            </span>
          ) : (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {String(row.attendance_status ?? "registered")}
            </span>
          )}
          {isGuest ? (
            isMember ? (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                Now a member
              </span>
            ) : (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                Guest
              </span>
            )
          ) : isMember ? (
            convertedHere ? (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                Converted here
              </span>
            ) : (
              <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                Member
              </span>
            )
          ) : null}
        </div>
      </div>

      {/* Rep-facing reward status (step 7) */}
      <div className="mt-2">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.cls}`}
        >
          {status.text}
        </span>
      </div>

      {row.needs_reward_fix &&
        (locked ? (
          // Event window closed: re-issuing an award is an operator action and is no longer possible,
          // so don't show a dead greyed button. State the status honestly instead of implying an action
          // the operator can take here.
          <p className="mt-2 text-xs text-gray-500">
            Award correction needed — the event's operator window has closed.
          </p>
        ) : (
          <button
            type="button"
            onClick={fixReward}
            disabled={busy}
            className="mt-2 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy
              ? "Working…"
              : `Re-issue correct award${row.suggested_offer_name ? ` (${row.suggested_offer_name})` : ""}`}
          </button>
        ))}

      {/* Reward value + operator +$20 ratchet + QR show/(re)send, for ANY checked-in registrant.
          The ratchet bootstraps a token + $0 base voucher for a voucher-less registrant, then applies
          fixed $20 steps capped at 2 (+$40 max). Server enforces the cap + authorization. Show QR
          brings the QR up on screen (attendee photographs it); Send QR emails the same token QR — it
          redeems for whatever the reward has been ratcheted to. */}
      {!!row.checked_in_at && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-900">
            Reward: ${Number(row.reward_value ?? 0)}
          </span>
          {noAddedValue ? null : canStartOrRatchet ? (
            <button
              type="button"
              onClick={addTwenty}
              disabled={busy || locked}
              className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? "…" : "+ $20"}
            </button>
          ) : (row.ratchets_remaining ?? 0) === 0 ? (
            <span className="text-xs text-gray-400">max reached</span>
          ) : null}
          {row.ratchet_count > 0 && (
            <span className="text-xs text-gray-500">
              {row.ratchet_count}/2 added
            </span>
          )}
          {canShowQr && (
            <button
              type="button"
              onClick={showQr}
              disabled={busy}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              title="Bring up the QR on screen so the attendee can take a picture of it"
            >
              {busy ? "…" : "Show QR"}
            </button>
          )}
          {canResendQr && (
            <button
              type="button"
              onClick={resendQr}
              disabled={busy || locked}
              className="rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 disabled:opacity-50"
              title="Email the attendee their QR ticket (it redeems for the current reward value)"
            >
              {busy ? "…" : "Send QR"}
            </button>
          )}
        </div>
      )}

      {/* Companion conversion → credit the inviter (seams 3-4). Shows only on a companion row (someone
          recorded who brought them) once they've bought a membership via THIS event's CTA. The button
          credits the inviting member's OWN reward via the same +$20 ratchet; if the inviter isn't
          checked in there's nothing to credit yet. */}
      {row.invited_by_name && row.membership_purchased && (
        <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2">
          <p className="text-xs font-medium text-emerald-800">
            ✓ Bought a membership at this event
          </p>
          {row.referral_credited ? (
            <p className="mt-1 text-xs font-medium text-emerald-700">
              ✓ Credited {row.invited_by_name} for this referral.
            </p>
          ) : noAddedValue ? null : row.inviter_attendee_id ? (
            <button
              type="button"
              onClick={creditInviter}
              disabled={busy || locked}
              className="mt-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? "…" : `Credit ${row.invited_by_name} (+$20)`}
            </button>
          ) : (
            <p className="mt-1 text-xs text-emerald-700">
              {row.invited_by_name} isn't checked in yet — have them check in to
              credit them.
            </p>
          )}
        </div>
      )}

      {/* On-screen QR — operator shows this so the attendee can photograph it. */}
      {qr && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setQr(null)}
        >
          <div
            className="w-full max-w-xs rounded-2xl bg-white p-5 text-center shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-gray-900">{name}</h3>
            <p className="mb-3 mt-0.5 text-xs text-gray-500">
              Have the attendee take a picture of this code.
            </p>
            {qr.url ? (
              <img
                src={qr.url}
                alt="Attendee ticket QR code"
                className="mx-auto h-56 w-56 rounded-lg border border-gray-200 object-contain"
              />
            ) : (
              <p className="text-sm text-gray-500">QR image unavailable.</p>
            )}
            {qr.code && (
              <div className="mt-3 font-mono text-lg font-bold tracking-wide text-gray-900">
                {qr.code}
              </div>
            )}
            <button
              type="button"
              onClick={() => setQr(null)}
              className="mt-4 w-full rounded-lg bg-gray-800 px-4 py-2 text-sm font-semibold text-white"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {row.contact_id && isMember && referralsAvailable && (
        <div className="mt-2">
          {!link ? (
            <button
              type="button"
              onClick={getReferral}
              disabled={busy}
              className="rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 disabled:opacity-50"
            >
              {busy ? "Working…" : "Forward to a friend"}
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                readOnly
                value={link}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-900"
              />
              <button
                type="button"
                onClick={copyLink}
                className="whitespace-nowrap rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          )}
        </div>
      )}

      {msg && <p className="mt-2 text-xs text-gray-600">{msg}</p>}
    </div>
  );
};

export default EventOperationsTab;
